import { Box, Checkbox, FormControl, MenuItem, Typography } from "@mui/material";
import InsuranceComponent from "./InsuranceComponent";
import { AppSelect, InputBase, serialNumberProps } from "components/common/Input";
import { GetFieldPropsType, Label } from "./SelectBox";
import React, { useCallback, useState } from "react";
import { defaultDevice } from "hooks/assetRetrievals/useAssetRetrieval";
import { AssetRetrieval } from "hooks/assetRetrievals/useAssetRetrieval";
import { BoxType, DeviceType } from "types/assetRetrieval";
import theme from "lib/theme";

const SimpleBoxInputs = ({
  getFieldProps,
  touched,
  errors,
  values,
  setFieldValue,
}: {
  getFieldProps: GetFieldPropsType;
  touched: Record<string, boolean>;
  errors: Record<string, string>;
  values: AssetRetrieval;
  setFieldValue: (field: string, value: any) => void;
}) => {
  const [includeMobileOrTablet, setIncludeMobileOrTablet] = useState<boolean>(false);

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIncludeMobileOrTablet(e.target.checked);

      const mainDevice = values.devices?.[0] || defaultDevice;

      if (!e.target.checked) {
        setFieldValue("devices", [mainDevice]);
      } else {
        setFieldValue("devices", [mainDevice, { deviceType: DeviceType.mobile_phone }]);
      }
    },
    [values, setFieldValue]
  );

  return (
    <>
      <Box mt={2} sx={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: 2 }}>
        <FormControl variant="standard">
          <Label>Manufacturer</Label>
          <InputBase
            type="text"
            {...getFieldProps<string>("devices.0.manufacturer")}
            error={touched.manufacturer && !!errors.manufacturer}
            helperText={touched.manufacturer && errors.manufacturer}
          />
        </FormControl>
        <FormControl variant="standard">
          <Label>Model</Label>
          <InputBase
            type="text"
            {...getFieldProps<string>("devices.0.model")}
            error={touched.model && !!errors.model}
            helperText={touched.model && errors.model}
          />
        </FormControl>
        <FormControl variant="standard">
          <Label>Serial No</Label>
          <InputBase
            {...serialNumberProps(getFieldProps<string>("devices.0.serialNumber"))}
            error={touched.serialNumber && !!errors.serialNumber}
            helperText={touched.serialNumber && errors.serialNumber}
          />
        </FormControl>
        <InsuranceComponent fullWidth={false} getFieldProps={getFieldProps} />
      </Box>

      {values.boxType && [BoxType.laptop, BoxType.tablet].includes(values.boxType as BoxType) && (
        <>
          <Box mt={2} sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              checked={includeMobileOrTablet}
              onChange={handleCheckboxChange}
              sx={{
                "&.Mui-checked": {
                  color: theme.palette.primary.main,
                },
                pl: 0,
                pr: 0.5,
                color: theme.palette.primary.main,
              }}
            />
            <Typography variant="body1">
              Include a mobile phone {values.boxType === BoxType.laptop && "or tablet"} in the box
            </Typography>
          </Box>

          {includeMobileOrTablet && (
            <Box mt={2} sx={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: 2 }}>
              <FormControl variant="standard">
                <Label>Device Type</Label>
                <AppSelect
                  {...getFieldProps<string>("devices.1.deviceType")}
                  value={values.devices?.[1]?.deviceType || DeviceType.mobile_phone}
                >
                  {values.boxType === BoxType.laptop && <MenuItem value={DeviceType.tablet}>Tablet</MenuItem>}
                  <MenuItem value={DeviceType.mobile_phone}>Mobile Phone</MenuItem>
                </AppSelect>
              </FormControl>

              <FormControl variant="standard">
                <Label>Serial No</Label>
                <InputBase {...serialNumberProps(getFieldProps<string>("devices.1.serialNumber"))} />
              </FormControl>

              <FormControl variant="standard">
                <Label>Manufacturer</Label>
                <InputBase type="text" {...getFieldProps<string>("devices.1.manufacturer")} />
              </FormControl>

              <FormControl variant="standard">
                <Label>Model</Label>
                <InputBase type="text" {...getFieldProps<string>("devices.1.model")} />
              </FormControl>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default SimpleBoxInputs;
